import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spin, Alert, Tabs } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
    Polyline,
    Tooltip,
    CircleMarker,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTrackVehicleQuery } from "./TripPlanTableNew";
import originWarehouseMarker from "../../assets/images/origin-warehouse.png";
import destinationWarehouseMarker from "../../assets/images/destination-warehouse.png";
import {
    GetRouteOptimizationTripService,
    GetTripPlanWaybillsByPlateNoService,
    GetVehicleTrackingHistoryService,
} from "../../services/trip-plan-service";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { TripPlanStatusEnum } from "../../utils/app-common";
import trailerActive from "../../assets/images/trailer-active.png";
import TabPane from "antd/lib/tabs/TabPane";
var polyline = require("@mapbox/polyline");

export const useGetTripTrackingDetailQuery = ({
    plateNo,
    enabled,
}: {
    plateNo: string;
    enabled: boolean;
}) => {
    const onRequest = async () => {
        const res = await GetTripPlanWaybillsByPlateNoService(plateNo);
        if (res?.status === 200 && res?.data?.statusCode === 200) {
            return res.data?.data || {};
        } else {
            return { error: "Failed to track vehicle" };
        }
    };

    return useQuery({
        queryKey: ["trip-tracking", plateNo],
        queryFn: () => onRequest(),
        enabled: !!plateNo && enabled,
    });
};

export const useGetTripRouteOptimizationQuery = (
    tripId: number,
    enabled: boolean
) => {
    const onRequest = async () => {
        const res = await GetRouteOptimizationTripService(tripId);
        if (res?.status === 200 && res?.data?.statusCode === 200) {
            return res.data?.data || [];
        } else {
            return { error: "Failed to track vehicle" };
        }
    };

    return useQuery({
        queryKey: ["trip-route-optimization", tripId],
        queryFn: onRequest,
        enabled: !!tripId && enabled,
    });
};

export const useGetVehicleTrackingHistoryQuery = (
    request: { id: string; fromDate: string; toDate: string },
    enabled: boolean
) => {
    const fetchHistory = async () => {
        const res = await GetVehicleTrackingHistoryService(request);
        if (res?.status === 200 && res?.data?.statusCode === 200) {
            return res.data?.data?.data || [];
        } else {
            return { error: "Failed to fetch vehicle tracking history" };
        }
    };

    return useQuery({
        queryKey: ["vehicle-tracking-history", request],
        queryFn: fetchHistory,
        enabled,
    });
};

const MapEffect: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
        map.setView([lat, lng], 7);
    }, [lat, lng, map]);
    return null;
};

function FitBoundsOnLoad({ bounds }) {
    const map = useMap();
    useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [map, bounds]);
    return null;
}

const RecenterControl: React.FC<{
    lat: number;
    lng: number;
    refetch: () => void;
}> = ({ lat, lng, refetch }) => {
    const map = useMap();

    useEffect(() => {
        const controlDiv = L.DomUtil.create(
            "div",
            "leaflet-bar leaflet-control leaflet-control-custom"
        );
        controlDiv.style.backgroundColor = "white";
        controlDiv.style.width = "80px";
        controlDiv.style.height = "30px";
        controlDiv.style.display = "flex";
        controlDiv.style.alignItems = "center";
        controlDiv.style.justifyContent = "center";
        controlDiv.style.cursor = "pointer";
        controlDiv.title = "Recenter and Refresh";
        controlDiv.innerHTML = "Re-position ⟳ ";

        controlDiv.onclick = () => {
            map.setView([lat, lng], map.getZoom());
            refetch();
        };

        const CustomControl = L.Control.extend({
            onAdd: () => controlDiv,
            onRemove: () => { },
        });

        const controlInstance = new CustomControl({ position: "topright" });
        map.addControl(controlInstance);

        return () => {
            map.removeControl(controlInstance);
        };
    }, [lat, lng, map, refetch]);

    return null;
};

const SetMapInstance: React.FC<{ onMapReady: (map: L.Map) => void }> = ({
    onMapReady,
}) => {
    const map = useMap();
    useEffect(() => {
        onMapReady(map);
    }, [map, onMapReady]);
    return null;
};

type TrackTripProps = {
    visible: boolean;
    handleCancel: () => void;
    record?: any;
};

const formatDateTime = (ms?: number | string) => {
    if (!ms) return "";
    return moment(Number(ms)).format("YYYY-MM-DD HH:mm:ss");
};

export const TrackTrip: React.FC<TrackTripProps> = ({
    visible,
    handleCancel,
    record,
}) => {
    const isArrivedAtDestination = ![
        TripPlanStatusEnum.LoadToDestination,
        TripPlanStatusEnum.OffloadAtTransit,
        TripPlanStatusEnum.LoadAtTransit,
    ].includes(record?.currentStatus);

    const req = {
        id: record?.vehicleTrackingNumber,
        fromDate: formatDateTime(record?.etd),
        toDate: formatDateTime(record?.ata || record?.eta),
    };

    const { data: tripData, isLoading, isFetching, refetch } = useTrackVehicleQuery({
        plateNo: record?.vehicleTrackingNumber,
        enabled: visible && !!record?.vehicleTrackingNumber,
    });

    const {
        data: tripTrackingData,
        isLoading: isTripTrackingLoading,
        isFetching: isTripTrackingFetching,
        refetch: refetchTripTracking,
    } = useGetTripTrackingDetailQuery({
        plateNo: record?.plateNumber,
        enabled: visible || !!record?.plateNumber,
    });

    const {
        data: tripRouteMapData = [],
        isLoading: isTripRouteMapLoading,
        refetch: refetchTripRouteMap,
    } = useGetVehicleTrackingHistoryQuery(req, visible && !!record?.vehicleTrackingNumber);

    const { data: optimizedTripRouteMapData = [], isFetching: isOptimizedTripRouteMapFetching, refetch: refetchOptimizedTripRouteMap } =
        useGetTripRouteOptimizationQuery(record?.id, visible);

    const decodedPath = useMemo(() => {
        const geometry = optimizedTripRouteMapData?.result?.routes?.[0]?.geometry;
        if (!geometry) return [];
        return polyline.decode(geometry).map(([lat, lng]: [number, number]) => [lat, lng]);
    }, [optimizedTripRouteMapData]);

    const steps = optimizedTripRouteMapData?.result?.routes?.[0]?.steps || [];

    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);

    useEffect(() => {
        if (visible && mapInstance) {
            const timer = setTimeout(() => {
                mapInstance.invalidateSize();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [visible, mapInstance]);

    const CHECKPOINT_INTERVAL = 15;

    const refresh = () => {
        refetch();
        refetchTripTracking();
        refetchTripRouteMap();
        refetchOptimizedTripRouteMap();
    };
    const [activeTab, setActiveTab] = useState("1");
    const renderContent = () => {
        if (isTripRouteMapLoading || isLoading || isOptimizedTripRouteMapFetching) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin />
                </div>
            );
        }

        const hasRouteData = Array.isArray(tripRouteMapData) && tripRouteMapData.length > 0;

        console.log(steps[0]?.location?.[0]);

        const defaultCenter: [number, number] =
            decodedPath.length > 0
                ? decodedPath[Math.floor(decodedPath.length / 2)]
                : hasRouteData
                    ? [tripRouteMapData[tripRouteMapData.length - 1].lat, tripRouteMapData[tripRouteMapData.length - 1].lng]
                    : [23.8859, 45.0792]; // fallback: Saudi center

        return (
            <div style={{ height: "100%", width: "100%" }}>
                <MapContainer
                    center={defaultCenter}
                    zoom={decodedPath.length > 0 ? 12 : hasRouteData ? 6 : 5}
                    style={{ height: "70vh", width: "100%" }}
                >
                    {decodedPath.length > 0 && <FitBoundsOnLoad bounds={decodedPath} />}
                    <SetMapInstance onMapReady={setMapInstance} />
                    <TileLayer
                        url="https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}"
                        attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                    />

                    {tripData?.lat && tripData?.lng && (
                        <>
                            <MapEffect lat={tripData.lat} lng={tripData.lng} />
                            <RecenterControl lat={tripData.lat} lng={tripData.lng} refetch={refresh} />
                        </>
                    )}

                    {/* Optimized Route Path */}
                    {decodedPath.length > 0 ? (
                        <>
                            <Polyline positions={decodedPath} pathOptions={{ color: "#128e19ff", weight: 6 }} />

                            {/* Start Marker */}
                            {steps[0]?.location?.[0]}
                            {(
                                <Marker
                                    position={steps[0].location}
                                    icon={L.icon({
                                        iconUrl: originWarehouseMarker,
                                        iconSize: [40, 40],
                                        iconAnchor: [20, 40],
                                    })}
                                >
                                    <Popup>Origin Hub</Popup>
                                </Marker>
                            )}

                            {/* End Marker */}
                            {(
                                <Marker
                                    position={steps[steps.length - 1].location || decodedPath[decodedPath.length - 1]}
                                    icon={L.icon({
                                        iconUrl: destinationWarehouseMarker,
                                        iconSize: [40, 40],
                                        iconAnchor: [20, 40],
                                    })}
                                >
                                    <Popup>Destination Hub</Popup>
                                </Marker>
                            )}
                        </>
                    ) : (
                        <Alert message="No optimized route geometry found" type="warning" showIcon />
                    )}

                    {/* Actual Trip History */}
                    {hasRouteData && (
                        <>
                            <Polyline
                                positions={tripRouteMapData.map((p) => [p.lat, p.lng])}
                                color={isArrivedAtDestination ? "red" : "red"}
                                weight={4}
                                opacity={0.8}
                            />

                            {tripRouteMapData.map((point, index) =>
                                index % CHECKPOINT_INTERVAL === 0 ? (
                                    <CircleMarker
                                        key={`checkpoint-${index}`}
                                        center={[point.lat, point.lng]}
                                        radius={2.5}
                                        color={isArrivedAtDestination ? "red" : "red"}
                                        fillColor="yellow"
                                        fillOpacity={0.9}
                                        stroke
                                        weight={2}
                                    >
                                        <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                                            <div style={{ fontSize: "12px" }}>
                                                <div><b>Date:</b> {moment(point.date).format("YYYY-MM-DD HH:mm:ss")}</div>
                                                <div><b>Odometer:</b> {point.odometer?.toFixed(2)} km</div>
                                                <div><b>Speed:</b> {point.speed} km/h</div>
                                                <div><b>Temp:</b> {point.temperature ?? "--"} °C</div>
                                            </div>
                                        </Tooltip>
                                    </CircleMarker>
                                ) : null
                            )}
                        </>
                    )}
                    {
                        !isArrivedAtDestination && tripRouteMapData.length >= 2 ? (() => {
                            const origin = tripRouteMapData[tripRouteMapData.length - 1];
                            const recent = tripRouteMapData[0];

                            const calculateBearing = (
                                lat1: number,
                                lon1: number,
                                lat2: number,
                                lon2: number
                            ): number => {
                                const toRad = (deg: number) => (deg * Math.PI) / 180;
                                const toDeg = (rad: number) => (rad * 180) / Math.PI;

                                const dLon = toRad(lon2 - lon1);
                                const y = Math.sin(dLon) * Math.cos(toRad(lat2));
                                const x =
                                    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
                                    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
                                const brng = Math.atan2(y, x);
                                return (toDeg(brng) + 360) % 360;
                            };

                            const bearing = calculateBearing(origin.lat, origin.lng, recent.lat, recent.lng);

                            // const bearing = 0;  0 : // North (up), 90: East (right), 180: South (down), 270: West (left)
                            // Adjust the bearing to match the icon rotation logic
                            let transform = "";

                            if (bearing >= 45 && bearing < 135) {
                                // East (right) – default
                                transform = "rotate(0deg)";
                            } else if (bearing >= 135 && bearing < 225) {
                                // South (down)
                                transform = "rotate(90deg)";
                            } else if (bearing >= 225 && bearing < 315) {
                                // West (left) – use mirror instead of rotate(180deg)
                                transform = "scaleX(-1)";
                            } else {
                                // North (up)
                                transform = "rotate(-90deg)";
                            }

                            const rotatedTrailerIcon = L.divIcon({
                                html: `<img src="${trailerActive}" style="transform: ${transform}; width:40px; height:40px;" />`,
                                iconSize: [40, 40],
                                className: "rotated-trailer-icon",
                            });


                            return (
                                <Marker position={[recent.lat, recent.lng]} icon={rotatedTrailerIcon}>
                                    <Popup>
                                        <Tabs
                                            activeKey={activeTab}
                                            onChange={setActiveTab}
                                            size="small"
                                            tabBarExtraContent={
                                                <Button
                                                    size="small"
                                                    icon={<ReloadOutlined />}
                                                    onClick={() =>
                                                        activeTab === "1" ? refetch() : refetchTripTracking()
                                                    }
                                                    style={{ marginRight: "8px" }}
                                                ></Button>
                                            }
                                        >
                                            <TabPane tab="Fleet Detail" key="1">
                                                <div
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "repeat(2, 1fr)",
                                                        gap: "16px",
                                                    }}
                                                >
                                                    {[
                                                        {
                                                            label: "Fleet Plate No",
                                                            value: tripData?.vehicleId,
                                                        },
                                                        { label: "Fleet Code", value: tripData?.fleetCode },
                                                        {
                                                            label: "Vehicle Speed",
                                                            value: `${tripData?.vehicleSpeed || "--"} km/h`,
                                                        },
                                                        {
                                                            label: "Vehicle Status",
                                                            value: tripData?.vehicleStatus || "--",
                                                        },
                                                    ].map(({ label, value }) => (
                                                        <div key={label}>
                                                            <div style={{ fontWeight: "bold" }}>{label}</div>
                                                            <div>{value || "--"}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </TabPane>

                                            <TabPane tab="Trip Detail" key="2">
                                                {isTripTrackingFetching || isTripTrackingLoading ? (
                                                    <Spin size="small" />
                                                ) : (
                                                    <div
                                                        style={{
                                                            maxHeight: "220px",
                                                            overflowY: "auto",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "grid",
                                                                gridTemplateColumns: "repeat(2, 1fr)",
                                                                gap: "16px",
                                                            }}
                                                        >
                                                            {[
                                                                {
                                                                    label: "Trip Plan Number",
                                                                    value: tripTrackingData?.tripPlanNumber,
                                                                },
                                                                {
                                                                    label: "Trip Code",
                                                                    value: tripTrackingData?.tripCode,
                                                                },
                                                                {
                                                                    label: "Origin City",
                                                                    value: tripTrackingData?.originCity,
                                                                },
                                                                {
                                                                    label: "Destination City",
                                                                    value: tripTrackingData?.destCity,
                                                                },
                                                                {
                                                                    label: "Waybill No",
                                                                    value: tripTrackingData?.waybillNo,
                                                                },
                                                                {
                                                                    label: "Plate Number",
                                                                    value: tripTrackingData?.plateNumber,
                                                                },
                                                                {
                                                                    label: "Driver Name",
                                                                    value: tripTrackingData?.driverName1,
                                                                },
                                                                {
                                                                    label: "Vehicle Plate No",
                                                                    value: tripTrackingData?.plateNumber,
                                                                },
                                                                {
                                                                    label: "ETD",
                                                                    value: tripTrackingData?.etdDate,
                                                                },
                                                                {
                                                                    label: "ETA",
                                                                    value: tripTrackingData?.etaDate,
                                                                },
                                                            ].map(({ label, value }) => (
                                                                <div key={label}>
                                                                    <div style={{ fontWeight: "bold" }}>{label}</div>
                                                                    <div>{value || "--"}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </TabPane>
                                        </Tabs>
                                    </Popup>
                                </Marker>
                            );
                        })() : null
                    }
                </MapContainer>
            </div>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span>
                        Trip Tracking Details <b>({record?.tripPlanNumber})</b>
                    </span>

                    {/* Actual Route */}
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "red",
                                display: "inline-block",
                                borderRadius: 2,
                            }}
                        ></span>
                        Actual Route
                    </span>

                    {/* Optimized Route */}
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "green",
                                display: "inline-block",
                                borderRadius: 2,
                            }}
                        ></span>
                        Optimized Route
                    </span>

                    <Button
                        loading={isLoading || isFetching}
                        icon={<ReloadOutlined />}
                        size="small"
                        onClick={refresh}
                    >
                        Refresh
                    </Button>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="close" onClick={handleCancel}>
                    Close
                </Button>,
            ]}
            width={1100}
            style={{ top: 20 }}
            maskClosable={false}
            destroyOnClose={true}
        >
            {renderContent()}
        </Modal>


    );
};
